import { z } from "zod";
import { colorSchema } from "./primitives.js";
import { limits } from "./limits.js";

export const captionWordSchema = z.object({
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
  text: z.string().min(1).max(120),
});

export const captionCueSchema = z
  .object({
    id: z.string().min(1).max(64),
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
    text: z.string().min(1).max(1000),
    /** Present when the transcription provider returned word-level timings. */
    words: z.array(captionWordSchema).max(200).optional(),
  })
  .refine((c) => c.end > c.start, "caption must end after it starts");

export const captionStyleSchema = z.object({
  fontFamily: z.string().max(120).default("Inter, system-ui, sans-serif"),
  fontSize: z.number().min(8).max(200).default(42),
  fontWeight: z.number().int().min(100).max(900).default(700),
  color: colorSchema.default("#ffffff"),
  backgroundColor: colorSchema.default("rgba(0,0,0,0.55)"),
  highlightColor: colorSchema.default("#7dd3fc"),
  position: z.enum(["top", "center", "bottom"]).default("bottom"),
  /** Distance from the chosen edge, as a fraction of frame height. */
  offset: z.number().min(0).max(0.5).default(0.08),
  maxWidth: z.number().min(0.2).max(1).default(0.8),
  uppercase: z.boolean().default(false),
  wordHighlight: z.boolean().default(true),
});

export const captionsSchema = z.object({
  enabled: z.boolean().default(true),
  style: captionStyleSchema.default({}),
  cues: z.array(captionCueSchema).max(limits.maxCaptions).default([]),
  /** Set for a language's captions; otherwise read from each cue's own text. */
  direction: z.enum(["ltr", "rtl"]).optional(),
});

export type CaptionCue = z.infer<typeof captionCueSchema>;
export type CaptionStyle = z.infer<typeof captionStyleSchema>;
export type Captions = z.infer<typeof captionsSchema>;
